import { NativeStackScreenProps } from "@react-navigation/native-stack";
import { TerminalSettingsRootParamList } from "../../../../types";
import React from "react";
import {
  CurrentUserV2,
  TerminalSessionDetail,
} from "@towersystems/roam-common/lib/generated-types";
import { useHasTransitionEnded } from "../../../../utilities/use-after-interactions";
import { useDatabase } from "@nozbe/watermelondb/hooks";
import {
  Button,
  FlatActionList,
  FlatActionListItem,
  Page,
  useWithAppState,
} from "@towersystems/roam-common-native";
import {
  MUTATION_SWITCH_ACTIVE_TERMINAL,
  QUERY_TERMINAL_SESSION_DETAILS,
} from "../../../../utilities/auth/constants";
import { useLazyQuery, useMutation } from "@apollo/client";
import Terminal from "../../../../utilities/data/model/Terminal";
import { View } from "react-native-ui-lib";
import { FlatListSearchableSkeleton } from "../../../../components/FlatListSearchableSkeleton";
import { Feather } from "@expo/vector-icons";
import colors from "react-native-ui-lib/src/style/colors";

export type SelectTerminalSettingScreenProps = NativeStackScreenProps<
  TerminalSettingsRootParamList,
  "SelectTerminalSettingScreen"
>;

export const SelectTerminalSettingScreen = ({
  navigation,
}: SelectTerminalSettingScreenProps) => {
  const [selectedTerminalId, setSelectedTerminalId] = React.useState<
    string | null
  >(null);

  const [sessionDetails, setSessionDetails] = React.useState<
    TerminalSessionDetail[] | undefined
  >(undefined);

  const database = useDatabase();
  const transitionEnded = useHasTransitionEnded(navigation);

  const { appState, updateAppState } = useWithAppState();

  const [queryTerminalSessionDetails] = useLazyQuery<{
    terminalSessionDetails: TerminalSessionDetail[];
  }>(QUERY_TERMINAL_SESSION_DETAILS);
  const [mutationSetActiveTerminal] = useMutation<{
    switchActiveTerminal: CurrentUserV2;
  }>(MUTATION_SWITCH_ACTIVE_TERMINAL);

  const [availableTerminals, setAvailableTerminals] = React.useState<
    Terminal[] | undefined
  >(undefined);

  React.useEffect(() => {
    database
      .get<Terminal>("terminals")
      .query()
      .fetch()
      .then((i) => setAvailableTerminals(i));
  }, []);

  React.useEffect(() => {
    queryTerminalSessionDetails()
      .then((res) => {
        setSessionDetails(res.data?.terminalSessionDetails ?? []);
      })
      .catch((err) => {
        console.error("error fetching terminal session details", err);
      });
  }, []);

  const handleOnSubmit = async () => {
    if (!selectedTerminalId) {
      return;
    }
    try {
      const result = await mutationSetActiveTerminal({
        variables: {
          terminalId: selectedTerminalId,
        },
      });

      if (!result.data?.switchActiveTerminal) {
        throw new Error("Failed to enable terminal");
      }

      updateAppState({
        terminalId: selectedTerminalId,
      });

      navigation.goBack();
    } catch (error) {
      console.error("Error enabling terminal:", error);
    }
  };

  const numberOfEnabledTerminals = availableTerminals?.filter(
    (t) => t.enabled,
  ).length;

  const noTerminals =
    availableTerminals === undefined || numberOfEnabledTerminals === 0;

  const title = noTerminals
    ? "No terminals available"
    : numberOfEnabledTerminals === 1
    ? "Select your terminal"
    : `Select from ${numberOfEnabledTerminals} available terminals`;

  const secondaryTitle = noTerminals
    ? "Please check your subscription or contact support for assistance."
    : "You have multiple terminals available. Please select the terminal you want to use for this session.";

  const items: FlatActionListItem[] = (
    availableTerminals?.map((terminal) => {
      const enabled = terminal.enabled;
      const disabled = !terminal.enabled;
      const isActive = selectedTerminalId === terminal.id;

      const warningText =
        enabled === false && isActive
          ? "Required to switch from disabled terminal!"
          : undefined;

      const matchingSessionDetail = sessionDetails?.find(
        (detail) => detail.terminalId === terminal.id,
      );

      const sessionsText = matchingSessionDetail
        ? `${matchingSessionDetail.numberOfActiveSessions} active session${
            matchingSessionDetail.numberOfActiveSessions > 1 ? "s" : ""
          }`
        : undefined;

      return {
        key: terminal.id,
        title: terminal.name,
        subTitle: warningText || sessionsText,
        onPress: () => setSelectedTerminalId(terminal.id),
        selected: isActive,
        disabled: disabled,
        componentRight: (icon: any) => {
          return isActive ? (
            <Feather name="check" size={24} color={colors.action} />
          ) : (
            <View />
          );
        },
      };
    }) || []
  ).filter((f) => f.disabled !== true);

  const footerMarkup = (
    <View row>
      <View marginR-10 flex>
        <Button
          disabled={!selectedTerminalId}
          label={"Continue"}
          isAction
          onPress={() => handleOnSubmit()}
        ></Button>
      </View>
    </View>
  );

  if (
    availableTerminals === undefined ||
    sessionDetails === undefined ||
    !transitionEnded
  ) {
    return <FlatListSearchableSkeleton searchbar={false} />;
  }

  return (
    <Page title={title} secondaryTitle={secondaryTitle} footer={footerMarkup}>
      <FlatActionList items={items} />
    </Page>
  );
};
